/**
 * navigation/components/Sidebar
 * ──────────────────────────────────────────────────────────────────────────
 * The console's nav: 214px expanded, 56px collapsed, `14px 10px` padding over
 * #FAFAFA with a #E4E4E4 right rule.
 *
 * Each item is a 15px line icon at 0.72 opacity, its label, and a count badge
 * in 10.5px mono. The active item takes a #F2F2F2 fill, an #E4E4E4 border and
 * ink text at weight 600 — the only place in the nav where anything is filled.
 *
 * COLLAPSED shows icons only, centred, with the label moved to the
 * accessibility label so a screen reader and a tooltip still have it. The
 * collapse is instant, not animated, and persisted — see ShellContext.
 *
 * BADGE COUNTS are the live figures, passed in by the shell. A zero renders as
 * nothing rather than as "0": a badge exists to say there is something waiting.
 *
 * WHAT IS DELIBERATELY ABSENT: the prototype's "Demo data" block (Load sample
 * org / Clear & show onboarding). It was scaffolding for a prototype with no
 * backend, and shipping a "clear the workspace" button in a cash subledger
 * would be a serious mistake.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { neutral } from '../../theme/colors';
import { radii } from '../../theme/radii';
import { chrome } from '../../theme/spacing';
import { mono, sans, size as fontSize } from '../../theme/typography';
import { ellipsisStyle, pointerCursor } from '../../theme/webStyle';
import { isWeb } from '../../lib/responsive';
import Icon from '../../components/Icon';
import IconButton from '../../components/IconButton';
import type { NavItem, ViewKey } from '../routes';

/** The live counts the badges show, by badge name. */
export type BadgeCounts = Partial<Record<NonNullable<NavItem['badge']>, number>>;

export interface SidebarProps {
  title: string;
  items: readonly NavItem[];
  active: ViewKey;
  onSelect: (view: ViewKey) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  counts: BadgeCounts;
  /** Rendered as an overlay panel rather than a docked column. */
  overlay?: boolean;
}

export function Sidebar({
  title,
  items,
  active,
  onSelect,
  collapsed,
  onToggleCollapse,
  counts,
  overlay = false,
}: SidebarProps) {
  // An overlay drawer is always expanded: there is no room to collapse to, and
  // a 56px overlay would be a strip of icons floating over the page.
  const expanded = overlay || !collapsed;

  return (
    <View
      style={[
        styles.nav,
        { width: expanded ? chrome.sidebarWidth : chrome.sidebarCollapsedWidth },
        overlay && styles.overlay,
      ]}
    >
      <View style={styles.titleRow}>
        {expanded && (
          <Text numberOfLines={1} style={styles.title}>
            {title}
          </Text>
        )}
        <IconButton
          icon={collapsed ? 'chevronRight' : 'chevronLeft'}
          onPress={onToggleCollapse}
          accessibilityLabel={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        />
      </View>

      <ScrollView contentContainerStyle={styles.items} showsVerticalScrollIndicator={false}>
        {items.map((item) => (
          <NavRow
            key={item.key}
            item={item}
            active={item.key === active}
            expanded={expanded}
            count={item.badge ? counts[item.badge] : undefined}
            onPress={() => onSelect(item.key)}
          />
        ))}
      </ScrollView>
    </View>
  );
}

/* ── One nav item ────────────────────────────────────────────────────────── */

interface NavRowProps {
  item: NavItem;
  active: boolean;
  expanded: boolean;
  count: number | undefined;
  onPress: () => void;
}

function NavRow({ item, active, expanded, count, onPress }: NavRowProps) {
  const [hovered, setHovered] = useState(false);
  // An inactive item lifts to #F7F7F7 on hover; the active one is already
  // filled, so it does not move.
  const hoverFill = hovered && isWeb && !active;

  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="button"
      // Collapsed, the label lives only here — so it must always be set.
      accessibilityLabel={item.label}
      accessibilityState={{ selected: active }}
      style={[
        styles.row,
        pointerCursor,
        {
          justifyContent: expanded ? 'space-between' : 'center',
          borderColor: active ? neutral.border : 'transparent',
          backgroundColor: active
            ? neutral.ruleLight
            : hoverFill
              ? neutral.ruleLighter
              : 'transparent',
        },
      ]}
    >
      <View style={styles.rowLeft}>
        <Icon
          name={item.icon}
          size={15}
          strokeWidth={1.3}
          color={active ? neutral.ink : neutral.ink2}
          // 0.72 sits the icon behind its label without adding a second grey.
          opacity={0.72}
        />
        {expanded && (
          <Text numberOfLines={1} style={[styles.label, active && styles.labelActive]}>
            {item.label}
          </Text>
        )}
      </View>

      {/* A zero is not news — no badge. */}
      {expanded && count !== undefined && count > 0 && (
        <Text style={styles.badge}>{count}</Text>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  nav: {
    flexShrink: 0,
    paddingVertical: chrome.sidebarPaddingY,
    paddingHorizontal: chrome.sidebarPaddingX,
    backgroundColor: neutral.surface2,
    borderRightWidth: 1,
    borderRightColor: neutral.border,
  },
  overlay: { position: 'absolute', top: 0, bottom: 0, left: 0, zIndex: 30 },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 6,
    paddingHorizontal: 4,
    paddingBottom: 10,
  },
  title: {
    ...mono('500'),
    fontSize: fontSize.eyebrow,
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: neutral.grey400,
    paddingLeft: 6,
    flexShrink: 1,
  },
  items: { gap: 2 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    paddingVertical: 7,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderRadius: radii.control,
  },
  rowLeft: { flexDirection: 'row', alignItems: 'center', gap: 9, minWidth: 0, flexShrink: 1 },
  label: {
    ...sans('400'),
    fontSize: fontSize.body,
    color: neutral.ink2,
    flexShrink: 1,
    ...ellipsisStyle,
  },
  labelActive: { ...sans('600'), color: neutral.ink },
  badge: { ...mono('400'), fontSize: fontSize.tableHeader, color: neutral.grey600, flexShrink: 0 },
});

export default Sidebar;
